import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { MuiFormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import { getPopupContentStyle } from './ChangeTicketClaimStatusPopup.styled';
import useChangeClaimStatusPopup from './hooks/useChangeTicketClaimStatusPopup';

type ChangeTicketClaimStatusPopupPropsT = {
  ticketId: string;
  claimStatusId: number;
  isOpen: boolean;
  onClose: NoopT;
};

export type ChangeTicketClaimStatusFormValuesT = {
  ticketId: string;
  claimStatusId: number;
};

const ChangeTicketClaimStatusPopup: FC<ChangeTicketClaimStatusPopupPropsT> = ({
  ticketId,
  claimStatusId,
  isOpen,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const {
    isChangingClaimStatus,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions,
    handleChangeTicketClaimStatus,
    handleClosePopup,
  } = useChangeClaimStatusPopup();

  const fromMethods = useForm<ChangeTicketClaimStatusFormValuesT>({
    defaultValues: {
      ticketId: ticketId,
      claimStatusId: claimStatusId,
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, setValue } = fromMethods;

  useEffect(() => {
    if (isOpen) {
      setValue('ticketId', ticketId);
      setValue('claimStatusId', claimStatusId);
    }
  }, [isOpen, ticketId, claimStatusId, setValue]);

  const selectedClaimStatusId = useWatch<ChangeTicketClaimStatusFormValuesT, 'claimStatusId'>({
    control,
    name: 'claimStatusId',
  });

  return (
    <Popup
      nested
      isOpen={isOpen}
      popupId={ScrollLockIdE.ChangeTicketClaimStatusPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      <Typography variant="h6" align="center" sx={{ mb: 2 }}>
        Change claim status
      </Typography>
      <FormProvider {...fromMethods}>
        <Box
          component="form"
          onSubmit={handleSubmit(data => handleChangeTicketClaimStatus(data, onClose))}
        >
          <Box sx={{ mb: 2 }}>
            <MuiFormSelect
              name="claimStatusId"
              label="Choose Claim status"
              placeholder="Claim statuses"
              options={claimStatusesOptions.map(o => ({ value: o.value, label: o.label }))}
              isLoading={isLoadingClaimStatusesOptions}
              required
            />
          </Box>
          <Box display="flex" justifyContent={isMobile ? 'center' : 'flex-end'} gap={1}>
            <Button variant="outlined" onClick={handleClosePopup(onClose)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isChangingClaimStatus || selectedClaimStatusId === claimStatusId}
            >
              {isChangingClaimStatus ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Popup>
  );
};

export default ChangeTicketClaimStatusPopup;
